"use client"

import { getAntdLocale, getSiteUrl } from "@repo/shared"
import { Suspense, useEffect, useState } from "react"
import { ConfigProvider } from "antd"

import { AnnouncementBanner } from "./_components/HeaderContentWithScroll/AnnouncementBanner"
import { TopUtilityBar } from "./_components/HeaderContentWithScroll/components/TopUtilityBar"
import { HeaderContentWithScrollBase } from "./_components/HeaderContentWithScroll/HeaderContentWithScrollClient"
import { useCurrentUrl } from "./_components/HeaderContentWithScroll/utils/useCurrentUrl"
import type { NavigationItem } from "./_components/MainNavigation/type"
import { ScrollHandler } from "./_components/ScrollHandler"
import type { HeaderProps, UtilityNavItem } from "./interface"
import { loadHeaderPersonalization } from "./loadHeaderPersonalization"

function CurrentUrlObserver({ onChange }: { onChange: (url: string) => void }) {
  const url = useCurrentUrl()
  useEffect(() => {
    onChange(url)
  }, [url, onChange])
  return null
}

type Personalization = Awaited<ReturnType<typeof loadHeaderPersonalization>>

export function StaticHeaderClient({
  locale,
  menuItems,
  utilityNav,
  ...props
}: HeaderProps & {
  locale: string
  menuItems: NavigationItem[]
  utilityNav: UtilityNavItem[]
}) {
  const [currentUrl, setCurrentUrl] = useState("")
  const [personalization, setPersonalization] = useState<Personalization | null>(null)
  useEffect(() => {
    let active = true
    loadHeaderPersonalization()
      .then((data) => {
        if (!active) return
        if (data.loginRequired) {
          window.location.replace(
            `${getSiteUrl("user")}/login?url=${encodeURIComponent(window.location.href)}&clearAuth=true`
          )
          return
        }
        setPersonalization(data)
      })
      .catch(() => {
        /* Keep public navigation usable when personalization is unavailable. */
      })
    return () => {
      active = false
    }
  }, [locale])
  const announcements = props.showAnnouncementBanner === false ? [] : personalization?.announcements || []
  const userInfo = personalization?.userInfo || null
  const transparent = props.transparentMode !== false
  return (
    <ScrollHandler>
      <ConfigProvider
        locale={getAntdLocale(locale)}
        theme={{
          token: {
            fontFamily:
              locale === "th"
                ? "var(--font-noto-sans-thai), var(--font-montserrat), var(--font-roboto), Arial, Helvetica, sans-serif"
                : "var(--font-montserrat), var(--font-roboto), Arial, Helvetica, sans-serif",
            colorPrimary: "#d7000f",
            borderRadius: 2,
          },
        }}
      >
        <style>{`:root { --announcement-height: ${announcements.length ? 84 : 48}px; }`}</style>
        <HeaderContentWithScrollBase
          currentUrl={currentUrl}
          {...props}
          menuItems={menuItems}
          userInfo={userInfo}
          initialHeaderMarginTopDesktop={transparent ? 0 : 105}
          initialHeaderMarginTopMobile={transparent ? 0 : 48}
        >
          <AnnouncementBanner announcements={announcements} />
          <Suspense fallback={null}>
            <TopUtilityBar utilityNav={utilityNav} userInfo={userInfo} />
            <CurrentUrlObserver onChange={setCurrentUrl} />
          </Suspense>
        </HeaderContentWithScrollBase>
      </ConfigProvider>
    </ScrollHandler>
  )
}
